/**
 * useSnsAuthPopup — SNS OAuth 팝업 공용 훅
 * 로그인 화면(SnsLoginPage)과 회원가입 화면(SignupEntryPage)에서 동일하게 사용한다.
 * 팝업 → AuthCallbackPage → postMessage 수신 → applySnsLoginResult로 로그인/가입 자동 분기.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import { fetchOAuthState, buildOAuthUrl, type SnsProvider } from '../api/social-auth';
import { applySnsLoginResult, SNS_AUTH_MESSAGE_TYPE, type SnsAuthMessage } from '../lib/sns-auth';

const OAUTH_STATE_KEY = 'oauth_state';
const POPUP_FEATURES  = 'width=480,height=640,menubar=no,toolbar=no,location=no,status=no';

export function useSnsAuthPopup() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const setAuth     = useAuthStore((s) => s.setAuth);

  const [loadingProvider, setLoadingProvider] = useState<SnsProvider | null>(null);
  const [snsError, setSnsError]               = useState<string | null>(null);

  // 팝업이 스스로 닫혔는지 감시하는 poll 핸들
  const pollRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // AuthCallbackPage(팝업)가 보내는 결과 수신
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return; // 위조 postMessage 방지
      const data = event.data as SnsAuthMessage | undefined;
      if (!data || data.type !== SNS_AUTH_MESSAGE_TYPE) return;

      stopPolling();
      setLoadingProvider(null);

      if (data.error) {
        setSnsError(data.error);
        return;
      }
      if (data.result) {
        applySnsLoginResult(data.result, navigate, setAuth, queryClient);
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      stopPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSns = async (provider: SnsProvider) => {
    setSnsError(null);
    setLoadingProvider(provider);

    // 팝업 차단 회피: window.open은 클릭 핸들러 내에서 await 이전에 동기 호출해야 함
    const popup = window.open('about:blank', 'snsLogin', POPUP_FEATURES);
    if (!popup) {
      setSnsError('팝업이 차단되었습니다. 브라우저 팝업 차단을 해제한 뒤 다시 시도해 주세요.');
      setLoadingProvider(null);
      return;
    }

    try {
      const state       = await fetchOAuthState();
      const redirectUri = `${window.location.origin}/auth/callback/${provider}`;

      // opener의 sessionStorage가 아니라 popup 자신의 sessionStorage에 써야 함.
      // window.open 시점에 opener→popup으로 스토리지가 "한 번만" 복사되므로,
      // 그 이후(await 후) opener 쪽에 쓰면 popup에는 반영되지 않는다.
      popup.sessionStorage.setItem(OAUTH_STATE_KEY, state);
      popup.location.href = buildOAuthUrl(provider, state, redirectUri);
    } catch {
      popup.sessionStorage.removeItem(OAUTH_STATE_KEY);
      popup.close();
      setSnsError('소셜 로그인을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.');
      setLoadingProvider(null);
      return;
    }

    // 사용자가 결과 없이 팝업을 그냥 닫아버린 경우 로딩 해제
    stopPolling();
    pollRef.current = window.setInterval(() => {
      if (popup.closed) {
        stopPolling();
        setLoadingProvider(null);
      }
    }, 500);
  };

  return { loadingProvider, snsError, setSnsError, handleSns, anyLoading: loadingProvider !== null };
}
